/**
 * SRC-20 signed-read orchestrator.
 *
 * Builds a type-`0x4A` envelope with `signed_read = true`, has the connected
 * EOA sign the messageVersion-2 EIP-712 TxSeismic typed data via
 * `eth_signTypedData_v4`, posts the envelope to `eth_call`, and decrypts the
 * AES-256-GCM response.
 *
 * Why not seismic-viem? Its `signedCall`/`sreadContract` path requires the
 * client's pre-configured account to match `from`, and in our setup the call
 * was reaching the contract as a *plain* eth_call (no envelope), which causes
 * Seismic to zero `from`, `isOwner(0)` returns false, and the contract reverts
 * with "caller is not a safe owner". Hand-building the envelope sidesteps that.
 */
import { bytesToHex, hexToBytes, type Hex } from 'viem'
import type { Eip1193Provider } from 'ethers'
import {
  encodeAad,
  encodeSigned,
  MESSAGE_VERSION_EIP712,
  rawEnvelope,
  splitSignature,
  txSeismicTypedData,
  type EnvelopeFields,
} from './envelope'
import { decryptResponse, encryptCalldata, randomBytes, randomKeypair } from './crypto'
import { ethCallRaw, fetchLatestBlock, fetchTeePubkey, getTransactionCount } from './rpc'

export interface SignedReadParams {
  chainId: number
  rpcUrl: string
  /** EOA that signs the messageVersion-2 envelope; recovered as `msg.sender`. */
  owner: Hex
  /** Wallet provider — used for `eth_signTypedData_v4`. */
  provider: Eip1193Provider
  /** Reader contract address. */
  to: Hex
  /** Plaintext calldata (already ABI-encoded). */
  calldata: Hex
}

/**
 * Returns the decrypted ABI-encoded return data (caller does its own decode).
 * Throws on any RPC/crypto/signing failure; caller is expected to map the
 * error to a user-facing message.
 */
/** Convert any rejection (incl. plain `{code,message}`) into a real Error. */
const wrap = (step: string, err: unknown): Error => {
  if (err instanceof Error) {
    try {
      err.message = `[${step}] ${err.message}`
      return err
    } catch {
      // DOMException (e.g. WebCrypto OperationError) has a getter-only `message`.
      const wrapped = new Error(`[${step}] ${err.name}: ${err.message}`)
      ;(wrapped as Error & { cause?: unknown }).cause = err
      return wrapped
    }
  }
  if (err && typeof err === 'object') {
    const e = err as { code?: number; message?: string; data?: unknown }
    const code = e.code !== undefined ? ` code=${e.code}` : ''
    const msg = typeof e.message === 'string' ? e.message : JSON.stringify(err)
    const wrapped = new Error(`[${step}]${code} ${msg}`)
    ;(wrapped as Error & { cause?: unknown }).cause = err
    return wrapped
  }
  return new Error(`[${step}] ${String(err)}`)
}

const runStep = async <T>(step: string, fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn()
  } catch (err) {
    throw wrap(step, err)
  }
}

export async function signedShieldedRead(args: SignedReadParams): Promise<Uint8Array> {
  const networkPk33 = await runStep('fetchTeePubkey', () => fetchTeePubkey(args.rpcUrl))
  const { sk: clientSk32, pk33: clientPk33 } = randomKeypair()
  const encryptionNonce = BigInt('0x' + bytesToHex(randomBytes(12)).slice(2))

  const block = await runStep('fetchLatestBlock', () => fetchLatestBlock(args.rpcUrl))
  const expiresAtBlock = block.number + 100n
  const nonce = await runStep('getTransactionCount', () => getTransactionCount(args.rpcUrl, args.owner))
  const clientPkHex = bytesToHex(clientPk33)

  // gas_price = 0 is MANDATORY for signed reads (spec §14 foot-gun #3).
  // gasLimit is generous; unused gas isn't charged for `signed_read=true`.
  const gasPrice = 0n
  const gas = 600_000n

  const aad = encodeAad({
    sender: args.owner,
    chainId: args.chainId,
    nonce,
    to: args.to,
    value: 0n,
    encryptionPubkey: clientPkHex,
    encryptionNonce,
    messageVersion: MESSAGE_VERSION_EIP712,
    recentBlockHash: block.hash,
    expiresAtBlock,
    signedRead: true,
  })
  const aadBytes = hexToBytes(aad)

  const ciphertext = await runStep('encryptCalldata', () =>
    encryptCalldata({
      plaintext: hexToBytes(args.calldata),
      networkPk33,
      clientSk32,
      encryptionNonce,
      aad: aadBytes,
    }),
  )

  // EIP-712 typed data must be built AFTER encryption — `input` is the ciphertext.
  const typedData = txSeismicTypedData({
    chainId: args.chainId,
    nonce,
    gasPrice,
    gasLimit: gas,
    to: args.to,
    value: 0n,
    input: ciphertext,
    encryptionPubkey: clientPkHex,
    encryptionNonce,
    recentBlockHash: block.hash,
    expiresAtBlock,
    signedRead: true,
  })

  // Sign directly via the EIP-1193 provider (NOT through @safe-global/safe-apps-provider —
  // that would wrap in a SafeMessage envelope and break the owner-gated reader).
  if (!args.provider || typeof args.provider.request !== 'function') {
    throw new Error('[eth_signTypedData_v4] connected wallet provider does not expose a `.request()` method')
  }
  const sig = (await runStep(
    'eth_signTypedData_v4',
    () =>
      args.provider.request({
        method: 'eth_signTypedData_v4',
        params: [args.owner, JSON.stringify(typedData)],
      }) as Promise<unknown>,
  )) as Hex
  if (!sig || typeof sig !== 'string' || !sig.startsWith('0x') || sig.length !== 132) {
    throw new Error(`[eth_signTypedData_v4] returned malformed sig: ${String(sig)}`)
  }
  const { yParity, r, s } = splitSignature(sig)

  const fields: EnvelopeFields = {
    chainId: args.chainId,
    nonce,
    gasPrice,
    gasLimit: gas,
    to: args.to,
    value: 0n,
    encryptionPubkey: clientPkHex,
    encryptionNonce,
    messageVersion: MESSAGE_VERSION_EIP712,
    recentBlockHash: block.hash,
    expiresAtBlock,
    signedRead: true,
    ciphertext,
  }
  const raw = rawEnvelope(encodeSigned(fields, yParity, r, s))

  const cipherResp = await runStep('eth_call', () => ethCallRaw(args.rpcUrl, raw))
  if (!cipherResp || cipherResp === '0x') return new Uint8Array()

  return runStep('decryptResponse', () =>
    decryptResponse({
      ciphertextHex: cipherResp,
      networkPk33,
      clientSk32,
      encryptionNonce,
      aad: aadBytes,
    }),
  )
}
